import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiHandler, GiftSummary } from '../handlers';
import { appQueryClient } from '../appQueryClient';

export const GIFT_SUMMARY_QUERY_KEY = 'currentGiftSummary';

export const giftSummaryQueryFn = async (): Promise<GiftSummary> => {
    const response = await ApiHandler.instance.donor.getGiftSummary();
    if (!response.success) {
        throw new Error('Failed to fetch gift summary');
    }
    return response.data.response.payload;
};

const useGiftSummaryQuery = (enabled: boolean) => {
    return useQuery({
        queryKey: [GIFT_SUMMARY_QUERY_KEY],
        queryFn: giftSummaryQueryFn,
        enabled,
        staleTime: 1000 * 60 * 5,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
};

export const invalidateGiftSummaryQuery = () => {
    return appQueryClient.invalidateQueries({
        queryKey: [GIFT_SUMMARY_QUERY_KEY],
    });
};

export const useGiftSummaryFailedOnMount = () => {
    const queryClient = useQueryClient();
    const [failedOnMount] = useState(
        () =>
            queryClient.getQueryState([GIFT_SUMMARY_QUERY_KEY])?.status ===
            'error',
    );
    return failedOnMount;
};

export const useGiftSummary = (enabled = false) => {
    const queryClient = useQueryClient();
    const { data, isFetched, isFetching, isLoading, isError, error, refetch } =
        useGiftSummaryQuery(enabled);

    const getGiftSummary = useCallback(
        () => queryClient.getQueryData<GiftSummary>([GIFT_SUMMARY_QUERY_KEY]),
        [queryClient],
    );

    const updateLocalGiftSummary = useCallback(
        (summary: GiftSummary | undefined) => {
            if (summary === undefined) {
                queryClient.removeQueries({
                    queryKey: [GIFT_SUMMARY_QUERY_KEY],
                });
                return;
            }
            queryClient.setQueryData<GiftSummary>(
                [GIFT_SUMMARY_QUERY_KEY],
                summary,
            );
        },
        [queryClient],
    );

    const incrementLocalTotalGiftsCount = useCallback(() => {
        const summary = queryClient.getQueryData<GiftSummary>([
            GIFT_SUMMARY_QUERY_KEY,
        ]);
        const totalGiftsCount = (summary?.all.count ?? 0) + 1;
        if (summary) {
            queryClient.setQueryData<GiftSummary>([GIFT_SUMMARY_QUERY_KEY], {
                ...summary,
                all: { ...summary.all, count: totalGiftsCount },
            });
        } else {
            refetch({ cancelRefetch: false });
        }
        return totalGiftsCount;
    }, [queryClient, refetch]);

    return {
        giftSummary: data,
        totalGiftsCount: data?.all.count,
        isFetching,
        isLoading,
        hasNeverFetched: !isFetched && !isFetching,
        isError,
        error,
        refetch,
        getGiftSummary,
        updateLocalGiftSummary,
        incrementLocalTotalGiftsCount,
    };
};
